/**
 * WorkflowPage Component
 *
 * This component implements the workflow selection interface for the Migration Utility application.
 * It provides cards for different migration types:
 * 1. Ontology Migration (active)
 * 2. Process Migration (coming soon)
 *
 * When a user selects Ontology Migration, it creates a new workflow and navigates to the detail page.
 *
 * @module pages/WorkflowPage
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Database, FileText, Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { useLogin, useGenerateCode } from "../lib/api";
import { useWorkflowStore } from "../store/useWorkflowStore";
import { cn } from "../lib/utils";
import { useToast } from "../lib/use-toast";
import { AppSidebar } from "../components/app-sidebar";
import { SiteHeader } from "../components/site-header";
import { SidebarInset, SidebarProvider } from "../components/ui/sidebar";

/**
 * WorkflowPage Component
 *
 * @returns {JSX.Element} The rendered WorkflowPage component
 */
const WorkflowPage: React.FC = () => {
  // Navigation hook
  const navigate = useNavigate();

  // Toast hook
  const { toast } = useToast();

  // Workflow store
  const { setCurrentWorkflow } = useWorkflowStore();
  
  // Code generation hook
  const { generateCode, isGenerating: isGeneratingCode } = useGenerateCode();

  /**
   * Handles the creation of a new ontology migration workflow
   */
  const handleCreateOntologyWorkflow = async () => {
    try {
      toast({
        title: "Creating workflow...",
        description: "Please wait while we set up your migration workflow.",
      });

      // Reset any existing workflow state first to ensure a clean start
      // This prevents issues with persisted state from previous workflows
      useWorkflowStore.getState().resetForm();

      // Generate a code for the workflow
      const codeData = await generateCode('ONTOLOGY');
      console.log('Generated code:', codeData);
      
      // Extract the code and name from the response
      const { code, name } = codeData as { code: string; name: string };
      
      // Set the current workflow in the store with the generated code and name
      setCurrentWorkflow({
        id: code, // Use the generated code as the ID
        name: name,
        type: 'ONTOLOGY',
      });
      
      // Navigate directly to the workflow detail page using the generated code
      navigate(`/workflows/ontology/${code}`);
    } catch (error) {
      console.error("Error creating workflow:", error);
      
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create workflow. Please try again.",
      });
    }
  };

  const handleCreateProcessWorkflow = async () => {
    try {
      toast({
        title: "Creating workflow...",
        description: "Please wait while we set up your migration workflow.",
      });

      // Reset any existing workflow state first to ensure a clean start
      // This prevents issues with persisted state from previous workflows
      useWorkflowStore.getState().resetForm();

      // Generate a code for the workflow
      const codeData = await generateCode('PROCESS');
      console.log('Generated code:', codeData);

      // Extract the code and name from the response
      const { code, name } = codeData as { code: string; name: string };

      // Set the current workflow in the store with the generated code and name
      setCurrentWorkflow({
        id: code, // Use the generated code as the ID
        name: name,
        type: 'PROCESS',
      });

      // Navigate directly to the workflow detail page using the generated code
      navigate(`/workflows/process/${code}`);
    } catch (error) {
      console.error("Error creating workflow:", error);

      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create workflow. Please try again.",
      });
    }
  };


  return (
    <SidebarProvider
      style={{
        "--sidebar-width": "16rem",
        "--header-height": "3rem",
      } as React.CSSProperties}
      className="min-h-screen"
    >
      <AppSidebar variant="inset" />
      <SidebarInset className="pt-0">
        <SiteHeader />
        <div className="flex flex-1 flex-col p-4">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground">Select Migration Type</h1>
            <p className="text-muted-foreground mt-1">
              Choose the type of migration you want to perform
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Ontology Migration Card */}
            <Card className="overflow-hidden hover:shadow-md transition-shadow border-primary/20">
              <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b border-primary/20 pb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-md">
                    <Database className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-semibold">Ontology Migration</CardTitle>
                    <CardDescription className="text-sm mt-1">
                      Migrate data structures, schemas, and relationships
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6 pt-5">
                <div className="flex flex-col space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 text-primary">
                      <svg width="18" height="18" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.49991 0.877045C3.84222 0.877045 0.877075 3.84219 0.877075 7.49988C0.877075 11.1575 3.84222 14.1227 7.49991 14.1227C11.1576 14.1227 14.1227 11.1575 14.1227 7.49988C14.1227 3.84219 11.1576 0.877045 7.49991 0.877045ZM1.82708 7.49988C1.82708 4.36686 4.36689 1.82704 7.49991 1.82704C10.6329 1.82704 13.1727 4.36686 13.1727 7.49988C13.1727 10.6329 10.6329 13.1727 7.49991 13.1727C4.36689 13.1727 1.82708 10.6329 1.82708 7.49988ZM10.1589 5.53774C10.3178 5.31191 10.2636 5.00001 10.0378 4.84109C9.81194 4.68217 9.50004 4.73642 9.34112 4.96225L6.51977 8.97154L5.35681 7.78706C5.16334 7.59002 4.84677 7.58711 4.64973 7.78058C4.45268 7.97404 4.44978 8.29061 4.64325 8.48765L6.22658 10.1003C6.33054 10.2062 6.47617 10.2604 6.62407 10.2483C6.77197 10.2363 6.90686 10.1591 6.99226 10.0377L10.1589 5.53774Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-foreground">Object types and their properties</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 text-primary">
                      <svg width="18" height="18" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.49991 0.877045C3.84222 0.877045 0.877075 3.84219 0.877075 7.49988C0.877075 11.1575 3.84222 14.1227 7.49991 14.1227C11.1576 14.1227 14.1227 11.1575 14.1227 7.49988C14.1227 3.84219 11.1576 0.877045 7.49991 0.877045ZM1.82708 7.49988C1.82708 4.36686 4.36689 1.82704 7.49991 1.82704C10.6329 1.82704 13.1727 4.36686 13.1727 7.49988C13.1727 10.6329 10.6329 13.1727 7.49991 13.1727C4.36689 13.1727 1.82708 10.6329 1.82708 7.49988ZM10.1589 5.53774C10.3178 5.31191 10.2636 5.00001 10.0378 4.84109C9.81194 4.68217 9.50004 4.73642 9.34112 4.96225L6.51977 8.97154L5.35681 7.78706C5.16334 7.59002 4.84677 7.58711 4.64973 7.78058C4.45268 7.97404 4.44978 8.29061 4.64325 8.48765L6.22658 10.1003C6.33054 10.2062 6.47617 10.2604 6.62407 10.2483C6.77197 10.2363 6.90686 10.1591 6.99226 10.0377L10.1589 5.53774Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-foreground">Relationships between objects</span>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="bg-muted border-t border-border p-4">
                <Button
                  onClick={handleCreateOntologyWorkflow}
                  disabled={isGeneratingCode}
                  variant="default"
                  className="w-full"
                >
                  {isGeneratingCode ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Start Ontology Migration"
                  )}
                </Button>
              </CardFooter>
            </Card>

            {/* Process Migration Card */}
            <Card className="overflow-hidden hover:shadow-md transition-shadow border-primary/20">
              <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b border-primary/20 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/10 p-2 rounded-md">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-semibold">Process Migration</CardTitle>
                      <CardDescription className="text-sm mt-1">
                        Migrate business processes and workflows
                      </CardDescription>
                    </div>
                  </div>
                  
                </div>
              </CardHeader>
              <CardContent className="p-6 pt-5">
                <div className="flex flex-col space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 text-primary">
                      <svg width="18" height="18" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.49991 0.877045C3.84222 0.877045 0.877075 3.84219 0.877075 7.49988C0.877075 11.1575 3.84222 14.1227 7.49991 14.1227C11.1576 14.1227 14.1227 11.1575 14.1227 7.49988C14.1227 3.84219 11.1576 0.877045 7.49991 0.877045ZM1.82708 7.49988C1.82708 4.36686 4.36689 1.82704 7.49991 1.82704C10.6329 1.82704 13.1727 4.36686 13.1727 7.49988C13.1727 10.6329 10.6329 13.1727 7.49991 13.1727C4.36689 13.1727 1.82708 10.6329 1.82708 7.49988ZM10.1589 5.53774C10.3178 5.31191 10.2636 5.00001 10.0378 4.84109C9.81194 4.68217 9.50004 4.73642 9.34112 4.96225L6.51977 8.97154L5.35681 7.78706C5.16334 7.59002 4.84677 7.58711 4.64973 7.78058C4.45268 7.97404 4.44978 8.29061 4.64325 8.48765L6.22658 10.1003C6.33054 10.2062 6.47617 10.2604 6.62407 10.2483C6.77197 10.2363 6.90686 10.1591 6.99226 10.0377L10.1589 5.53774Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-foreground">Business process definitions</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 text-primary">
                      <svg width="18" height="18" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.49991 0.877045C3.84222 0.877045 0.877075 3.84219 0.877075 7.49988C0.877075 11.1575 3.84222 14.1227 7.49991 14.1227C11.1576 14.1227 14.1227 11.1575 14.1227 7.49988C14.1227 3.84219 11.1576 0.877045 7.49991 0.877045ZM1.82708 7.49988C1.82708 4.36686 4.36689 1.82704 7.49991 1.82704C10.6329 1.82704 13.1727 4.36686 13.1727 7.49988C13.1727 10.6329 10.6329 13.1727 7.49991 13.1727C4.36689 13.1727 1.82708 10.6329 1.82708 7.49988ZM10.1589 5.53774C10.3178 5.31191 10.2636 5.00001 10.0378 4.84109C9.81194 4.68217 9.50004 4.73642 9.34112 4.96225L6.51977 8.97154L5.35681 7.78706C5.16334 7.59002 4.84677 7.58711 4.64973 7.78058C4.45268 7.97404 4.44978 8.29061 4.64325 8.48765L6.22658 10.1003C6.33054 10.2062 6.47617 10.2604 6.62407 10.2483C6.77197 10.2363 6.90686 10.1591 6.99226 10.0377L10.1589 5.53774Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-foreground">Workflow rules and conditions</span>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="bg-muted border-t border-border p-4">
                <Button
                    onClick={handleCreateProcessWorkflow}
                    disabled={isGeneratingCode}
                    variant="default"
                    className="w-full"
                >
                  {isGeneratingCode ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                  ) : (
                      "Start Process Migration"
                  )}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default WorkflowPage;
